import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Banknote, Undo2 } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDate, fmtNum } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

const errMsg = (e) => e?.response?.data?.detail || "Erreur";

const ApplyForm = ({ nc, remaining, onDone }) => {
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/credit-notes/${nc.id}/open-invoices`).then(({ data }) => setInvoices(data || [])).catch(() => setInvoices([]));
  }, [nc.id, nc.applied_total]);
  const selected = invoices.find((i) => i.id === invoiceId);
  const max = selected ? Math.min(remaining, selected.amount_due) : remaining;
  const pick = (id) => {
    setInvoiceId(id);
    const inv = invoices.find((i) => i.id === id);
    if (inv) setAmount(Math.min(remaining, inv.amount_due).toFixed(2));
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/credit-notes/${nc.id}/apply-to-invoice`, { invoice_id: invoiceId, amount: Number(amount) });
      toast.success(`Avoir imputé sur ${selected?.reference}`);
      setInvoiceId(""); setAmount("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!invoices.length) {
    return <p className="text-xs text-[#575A57]" data-testid="cn-no-open-invoice">Aucune facture ouverte pour ce client.</p>;
  }
  return (
    <div className="flex items-end gap-2 flex-wrap">
      <div className="flex-1 min-w-[220px]">
        <Label className="text-xs">Facture ouverte</Label>
        <Select value={invoiceId} onValueChange={pick}>
          <SelectTrigger data-testid="cn-apply-invoice"><SelectValue placeholder="Choisir une facture…" /></SelectTrigger>
          <SelectContent>
            {invoices.map((i) => (
              <SelectItem key={i.id} value={i.id}>{i.reference}{i.status === "BROUILLON" ? " (brouillon)" : ""} · {fmtDate(i.issue_date)} · reste {fmtNum(i.amount_due)} CHF</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-32">
        <Label className="text-xs">Montant (max {fmtNum(max)})</Label>
        <Input type="number" step="0.05" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cn-apply-amount" />
      </div>
      <Button onClick={submit} disabled={busy || !invoiceId || !(Number(amount) > 0) || Number(amount) > max + 0.004} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="cn-apply-submit">
        <Receipt className="w-4 h-4 mr-1.5" /> Imputer
      </Button>
    </div>
  );
};

const RefundForm = ({ nc, remaining, onDone }) => {
  const [method, setMethod] = useState("VIREMENT");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/credit-notes/${nc.id}/refund`, { method, refunded_at: date });
      toast.success("Avoir marqué remboursé");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2 flex-wrap">
      <div className="w-40">
        <Label className="text-xs">Mode</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="cn-refund-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="VIREMENT">Virement</SelectItem>
            <SelectItem value="ESPECES">Espèces</SelectItem>
            <SelectItem value="TWINT">TWINT</SelectItem>
            <SelectItem value="AUTRE">Autre</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-40">
        <Label className="text-xs">Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cn-refund-date" />
      </div>
      <Button onClick={submit} disabled={busy} variant="outline" data-testid="cn-refund-submit">
        <Banknote className="w-4 h-4 mr-1.5" /> Marquer remboursé ({fmtNum(remaining)} CHF)
      </Button>
    </div>
  );
};

const History = ({ nc, onDone }) => {
  const undoRefund = async () => {
    try { await api.delete(`/credit-notes/${nc.id}/refund`); toast.success("Remboursement annulé"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const apps = nc.applications || [];
  if (!apps.length && !(nc.refunded_amount > 0)) return null;
  return (
    <div className="space-y-1 text-sm" data-testid="cn-settlement-history">
      {apps.map((a) => (
        <div key={a.payment_id} className="flex justify-between">
          <span>Imputé sur <Link to={`/factures/${a.invoice_id}`} className="underline font-mono">{a.invoice_reference}</Link> · {fmtDate(a.applied_at)}</span>
          <span className="font-mono">{fmtNum(a.amount)} CHF</span>
        </div>
      ))}
      {nc.refunded_amount > 0 && (
        <div className="flex justify-between items-center">
          <span>Remboursé ({nc.refund_method}) le {fmtDate(nc.refunded_at)}
            <Button size="sm" variant="ghost" className="ml-2 h-6 text-xs" onClick={undoRefund} data-testid="cn-refund-undo"><Undo2 className="w-3 h-3 mr-1" /> Annuler</Button>
          </span>
          <span className="font-mono">{fmtNum(nc.refunded_amount)} CHF</span>
        </div>
      )}
      <p className="text-[11px] text-[#575A57]">Pour retirer une imputation, supprimez le paiement « AVOIR » sur la facture concernée.</p>
    </div>
  );
};

export const CreditNoteSettlement = ({ nc, onDone }) => {
  if (!["ENVOYEE", "EXECUTEE"].includes(nc.status)) return null;
  const remaining = Math.round((Number(nc.total_ttc || 0) - Number(nc.applied_total || 0) - Number(nc.refunded_amount || 0)) * 100) / 100;
  return (
    <Card data-testid="cn-settlement">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#575A57] uppercase tracking-wide">Règlement de l&apos;avoir</p>
          <p className="text-sm">Solde disponible : <span className="font-mono font-semibold text-[#8B3A1F]" data-testid="cn-remaining">{fmtNum(remaining)} CHF</span></p>
        </div>
        <History nc={nc} onDone={onDone} />
        {remaining > 0.004 && (
          <>
            <ApplyForm nc={nc} remaining={remaining} onDone={onDone} />
            <RefundForm nc={nc} remaining={remaining} onDone={onDone} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditNoteSettlement;
